/**
 * Donut / pie chart for ratio visualization.
 * Renders a Recharts PieChart with inner radius for the donut hole.
 */

import {
  Cell,
  Pie,
  PieChart,
  type PieLabelRenderProps,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

type Slice = {
  readonly name: string;
  readonly value: number;
  readonly color: string;
};

type Props = {
  readonly data: ReadonlyArray<Slice>;
  readonly size?: number;
};

const DEFAULT_SIZE = 200;
const RADIAN = Math.PI / 180;
const LABEL_OFFSET = 1.2;

function renderLabel(props: PieLabelRenderProps): React.JSX.Element {
  const cx = Number(props.cx ?? 0);
  const cy = Number(props.cy ?? 0);
  const midAngle = Number(props.midAngle ?? 0);
  const innerRadius = Number(props.innerRadius ?? 0);
  const outerRadius = Number(props.outerRadius ?? 0);
  const percent = Number(props.percent ?? 0);

  const radius = innerRadius + (outerRadius - innerRadius) * LABEL_OFFSET;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill="var(--color-text)"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
    >
      {`${(percent * 100).toFixed(0)}%`}
    </text>
  );
}

const TOOLTIP_STYLE: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid var(--color-border)",
  borderRadius: 6,
  padding: "8px 12px",
  fontSize: 13,
};

export function DonutChart({ data, size }: Props): React.JSX.Element {
  const chartSize = size ?? DEFAULT_SIZE;

  return (
    <ResponsiveContainer width="100%" height={chartSize}>
      <PieChart>
        <Pie
          data={data as Slice[]}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius="55%"
          outerRadius="80%"
          label={renderLabel}
          labelLine={false}
        >
          {data.map((slice) => (
            <Cell key={slice.name} fill={slice.color} />
          ))}
        </Pie>
        <Tooltip contentStyle={TOOLTIP_STYLE} />
      </PieChart>
    </ResponsiveContainer>
  );
}
